import { useEffect, useRef, useState } from "react";

// Screen Wake Lock API (fev/2026).
//
// Mantém a tela do celular acesa e impede que o navegador suspenda a aba
// enquanto o entregador estiver com uma entrega ativa (em_transito).
//
// Comportamento:
//   - Solicita wake lock quando `active=true`.
//   - Libera automaticamente quando `active=false` ou no unmount.
//   - Reaquire quando o app volta ao foreground (o browser libera o lock
//     automaticamente quando o documento fica hidden — precisa reobter).
//
// Retorna { supported, locked, error }.
//   - supported: browser tem a API disponível
//   - locked:    lock atualmente ativo
//   - error:     última mensagem de erro (ou null)
export function useWakeLock(active) {
  const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;
  const sentinelRef = useRef(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;

    const request = async () => {
      if (cancelled || !active) return;
      if (document.visibilityState !== "visible") return; // Só rola se aba visível
      if (sentinelRef.current) return; // Já tem lock
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await sentinel.release(); } catch { /* noop */ }
          return;
        }
        sentinelRef.current = sentinel;
        setLocked(true);
        setError(null);
        sentinel.addEventListener("release", () => {
          // Sistema pode liberar quando a aba vai pra background
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            setLocked(false);
          }
        });
      } catch (e) {
        setError(e?.message || String(e));
        setLocked(false);
      }
    };

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      setLocked(false);
      if (s) {
        try { await s.release(); } catch { /* noop */ }
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && active) {
        request();
      }
    };

    if (active) {
      request();
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [active, supported]);

  return { supported, locked, error };
}
